import type {
	ComponentProps,
	FocusEventHandler,
	HTMLAttributes,
	PointerEventHandler,
} from 'react';
import React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useResolvedPortalContainer } from '../../hooks/use-portal-container';
import { cn, Icon } from '@lqa-frontend/ui-system';
import { buttonVariants } from '../buttons/Button';

function DropdownMenuRoot({
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.Root>) {
	return (
		<DropdownMenuPrimitive.Root
			data-slot="dropdown-menu"
			data-testid="dropdown-menu"
			{...props}
		/>
	);
}

function DropdownMenuPortal({
	container,
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
	const resolvedContainer = useResolvedPortalContainer(container);
	return (
		<DropdownMenuPrimitive.Portal
			data-slot="dropdown-menu-portal"
			{...props}
			container={resolvedContainer}
		/>
	);
}

type DropdownMenuTriggerSize = 's' | 'm';

interface IDropdownMenuTriggerProps extends ComponentProps<
	typeof DropdownMenuPrimitive.Trigger
> {
	size?: DropdownMenuTriggerSize;
	placeholder?: string;
}

function DropdownMenuTrigger({
	className,
	size = 'm',
	placeholder,
	children,
	asChild,
	...props
}: IDropdownMenuTriggerProps) {
	if (asChild) {
		return (
			<DropdownMenuPrimitive.Trigger
				data-slot="dropdown-menu-trigger"
				data-testid="dropdown-menu-trigger"
				asChild
				{...props}
			>
				{children}
			</DropdownMenuPrimitive.Trigger>
		);
	}
	const hasValue =
		children !== undefined && children !== null && children !== '';

	return (
		<DropdownMenuPrimitive.Trigger
			data-slot="dropdown-menu-trigger"
			data-testid="dropdown-menu-trigger"
			className={cn(
				buttonVariants({ variant: 'outline', size }),
				'group w-full justify-between',
				'hover:border-interactive hover:bg-brand-blue-50',
				'data-[state=open]:border-interactive',
				className
			)}
			{...props}
		>
			<span className={cn('truncate', !hasValue && 'text-zinc-400 text-14')}>
				{hasValue ? children : placeholder}
			</span>
			<Icon
				name="chevronDown"
				size="s"
				className="shrink-0 text-text-secondary transition-transform group-data-[state=open]:rotate-180"
			/>
		</DropdownMenuPrimitive.Trigger>
	);
}

interface IDropdownMenuContentProps extends ComponentProps<
	typeof DropdownMenuPrimitive.Content
> {
	shouldPreventCloseAutoFocus?: boolean;
	onOpenAutoFocus?: (event: Event) => void;
	onPointerEnterCapture?: PointerEventHandler<HTMLDivElement>;
	onFocusCapture?: FocusEventHandler<HTMLDivElement>;
	portalContainer?: HTMLElement | null;
}

function DropdownMenuContent({
	className,
	align = 'start',
	sideOffset = 4,
	shouldPreventCloseAutoFocus = true,
	onCloseAutoFocus,
	portalContainer,
	...props
}: Readonly<IDropdownMenuContentProps>) {
	const resolvedContainer = useResolvedPortalContainer(portalContainer);
	return (
		<DropdownMenuPrimitive.Portal container={resolvedContainer}>
			<DropdownMenuPrimitive.Content
				data-slot="dropdown-menu-content"
				sideOffset={sideOffset}
				align={align}
				onCloseAutoFocus={(event) => {
					onCloseAutoFocus?.(event);
					// By default, don't put focus back on the trigger when closing.
					if (shouldPreventCloseAutoFocus) {
						event.preventDefault();
					}
				}}
				className={cn(
					'z-[100] min-w-160 overflow-hidden rounded-m border border-divider bg-base-1 text-text-primary shadow-lg origin-[var(--radix-dropdown-menu-content-transform-origin)]',
					// Open / close animation — a clear "drop" rather than the
					// default 8px settle. Slide distance is bumped to 12px
					// (slide-in-from-*-3) and duration to 200ms so the
					// dropdown reads as anchored to its trigger and falls
					// into place. Origin uses the Radix CSS var so the
					// scale animation pivots from where the trigger meets
					// the popup edge (e.g. top-left for `side=bottom
					// align=start`).
					'data-[state=open]:animate-in data-[state=closed]:animate-out',
					'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
					'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
					'data-[state=open]:duration-200 data-[state=closed]:duration-150',
					'data-[side=bottom]:slide-in-from-top-3',
					'data-[side=top]:slide-in-from-bottom-3',
					'data-[side=left]:slide-in-from-right-3',
					'data-[side=right]:slide-in-from-left-3',
					'data-[state=closed]:data-[side=bottom]:slide-out-to-top-3',
					'data-[state=closed]:data-[side=top]:slide-out-to-bottom-3',
					'data-[state=closed]:data-[side=left]:slide-out-to-right-3',
					'data-[state=closed]:data-[side=right]:slide-out-to-left-3',
					className
				)}
				data-testid="dropdown-menu-content"
				{...props}
			/>
		</DropdownMenuPrimitive.Portal>
	);
}

function DropdownMenuGroup({
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.Group>) {
	return (
		<DropdownMenuPrimitive.Group
			data-slot="dropdown-menu-group"
			data-testid="dropdown-menu-group"
			{...props}
		/>
	);
}

interface IDropdownMenuItemProps extends ComponentProps<
	typeof DropdownMenuPrimitive.Item
> {
	isInset?: boolean;
	isSelected?: boolean;
}

function DropdownMenuItem({
	className,
	isInset,
	isSelected,
	children,
	...props
}: IDropdownMenuItemProps) {
	return (
		<DropdownMenuPrimitive.Item
			data-slot="dropdown-menu-item"
			data-testid="dropdown-menu-item"
			data-selected={isSelected || undefined}
			className={cn(
				'relative flex cursor-pointer select-none items-center gap-8 rounded-s px-12 py-10 text-14 outline-none hover:bg-interactive-light hover:text-text-primary focus:bg-interactive-light focus:text-text-primary data-[highlighted]:bg-interactive-light data-[highlighted]:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
				isInset && 'pl-32',
				isSelected && 'bg-interactive-light font-semibold text-text-primary',
				className
			)}
			{...props}
		>
			<span className="min-w-0 truncate">{children}</span>
			{isSelected && <Icon name="check" size="s" className="shrink-0" />}
		</DropdownMenuPrimitive.Item>
	);
}

interface IDropdownMenuCheckboxItemProps extends ComponentProps<
	typeof DropdownMenuPrimitive.CheckboxItem
> {
	isInset?: boolean;
}

function DropdownMenuCheckboxItem({
	className,
	children,
	checked,
	isInset,
	...props
}: IDropdownMenuCheckboxItemProps) {
	return (
		<DropdownMenuPrimitive.CheckboxItem
			data-slot="dropdown-menu-checkbox-item"
			className={cn(
				'group relative flex cursor-pointer select-none items-center gap-8 rounded-s px-12 py-10 text-14 outline-none hover:bg-interactive-light hover:text-text-primary focus:bg-interactive-light focus:text-text-primary data-[highlighted]:bg-interactive-light data-[highlighted]:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
				isInset && 'pl-32',
				className
			)}
			checked={checked}
			data-testid="dropdown-menu-checkbox-item"
			{...props}
		>
			<span
				className="pointer-events-none flex size-16 shrink-0 items-center justify-center rounded-xs border border-divider-bold group-data-[state=checked]:border-interactive group-data-[state=checked]:bg-interactive"
				data-slot="dropdown-menu-checkbox-item-indicator"
			>
				<DropdownMenuPrimitive.ItemIndicator className="flex items-center justify-center">
					<Icon name="check" size="s" className="text-text-white shrink-0" />
				</DropdownMenuPrimitive.ItemIndicator>
			</span>
			<span className="min-w-0 w-full truncate">{children}</span>
		</DropdownMenuPrimitive.CheckboxItem>
	);
}

function DropdownMenuRadioGroup({
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
	return (
		<DropdownMenuPrimitive.RadioGroup
			data-slot="dropdown-menu-radio-group"
			data-testid="dropdown-menu-radio-group"
			{...props}
		/>
	);
}

interface IDropdownMenuRadioItemProps extends ComponentProps<
	typeof DropdownMenuPrimitive.RadioItem
> {
	isInset?: boolean;
}

function DropdownMenuRadioItem({
	className,
	children,
	isInset,
	...props
}: IDropdownMenuRadioItemProps) {
	return (
		<DropdownMenuPrimitive.RadioItem
			data-slot="dropdown-menu-radio-item"
			data-testid="dropdown-menu-radio-item"
			className={cn(
				'group relative flex cursor-default select-none items-center gap-8 rounded-s px-12 py-10 text-14 outline-none hover:bg-interactive-light hover:text-text-primary focus:bg-interactive-light focus:text-text-primary data-[highlighted]:bg-interactive-light data-[highlighted]:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
				isInset && 'pl-32',
				className
			)}
			{...props}
		>
			<span
				className="pointer-events-none flex size-16 shrink-0 items-center justify-center rounded-max border border-divider-bold group-data-[state=checked]:border-interactive"
				data-slot="dropdown-menu-radio-item-indicator"
			>
				<DropdownMenuPrimitive.ItemIndicator className="flex items-center justify-center">
					<span className="size-8 rounded-max bg-interactive" />
				</DropdownMenuPrimitive.ItemIndicator>
			</span>
			<span className="min-w-0 w-full truncate">{children}</span>
		</DropdownMenuPrimitive.RadioItem>
	);
}

interface IDropdownMenuLabelProps extends ComponentProps<
	typeof DropdownMenuPrimitive.Label
> {
	isInset?: boolean;
}

function DropdownMenuLabel({
	className,
	isInset,
	...props
}: IDropdownMenuLabelProps) {
	return (
		<DropdownMenuPrimitive.Label
			data-slot="dropdown-menu-label"
			data-testid="dropdown-menu-label"
			className={cn(
				'px-12 py-10 text-14 font-medium text-text-secondary',
				isInset && 'pl-32',
				className
			)}
			{...props}
		/>
	);
}

function DropdownMenuSeparator({
	className,
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
	return (
		<DropdownMenuPrimitive.Separator
			data-slot="dropdown-menu-separator"
			data-testid="dropdown-menu-separator"
			className={cn('-mx-4 my-4 h-px bg-divider', className)}
			{...props}
		/>
	);
}

function DropdownMenuShortcut({
	className,
	...props
}: HTMLAttributes<HTMLSpanElement>) {
	return (
		<span
			className={cn('ml-auto text-12 tracking-widest opacity-60', className)}
			data-testid="dropdown-menu-shortcut"
			{...props}
		/>
	);
}

function DropdownMenuSub({
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
	return (
		<DropdownMenuPrimitive.Sub
			data-slot="dropdown-menu-sub"
			data-testid="dropdown-menu-sub"
			{...props}
		/>
	);
}

interface IDropdownMenuSubTriggerProps extends ComponentProps<
	typeof DropdownMenuPrimitive.SubTrigger
> {
	isInset?: boolean;
}

function DropdownMenuSubTrigger({
	className,
	isInset,
	children,
	...props
}: IDropdownMenuSubTriggerProps) {
	return (
		<DropdownMenuPrimitive.SubTrigger
			data-slot="dropdown-menu-sub-trigger"
			data-testid="dropdown-menu-sub-trigger"
			className={cn(
				'flex cursor-default select-none items-center rounded-s px-12 py-10 text-14 outline-none focus:bg-interactive-light data-[state=open]:bg-interactive-light',
				isInset && 'pl-32',
				className
			)}
			{...props}
		>
			{children}
			<Icon name="chevronRight" size="s" className="ml-auto" />
		</DropdownMenuPrimitive.SubTrigger>
	);
}

function DropdownMenuSubContent({
	className,
	...props
}: ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
	return (
		<DropdownMenuPrimitive.SubContent
			data-slot="dropdown-menu-sub-content"
			data-testid="dropdown-menu-sub-content"
			className={cn(
				'z-[100] min-w-160 overflow-hidden rounded-m border border-divider bg-base-1 text-text-primary shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
				className
			)}
			{...props}
		/>
	);
}

const DropdownMenu = Object.assign(DropdownMenuRoot, {
	Portal: DropdownMenuPortal,
	Trigger: DropdownMenuTrigger,
	Content: DropdownMenuContent,
	Group: DropdownMenuGroup,
	Label: DropdownMenuLabel,
	Item: DropdownMenuItem,
	CheckboxItem: DropdownMenuCheckboxItem,
	RadioGroup: DropdownMenuRadioGroup,
	RadioItem: DropdownMenuRadioItem,
	Separator: DropdownMenuSeparator,
	Shortcut: DropdownMenuShortcut,
	Sub: DropdownMenuSub,
	SubTrigger: DropdownMenuSubTrigger,
	SubContent: DropdownMenuSubContent,
});

export { DropdownMenu };
export type {
	IDropdownMenuContentProps,
	IDropdownMenuItemProps,
	IDropdownMenuCheckboxItemProps,
	IDropdownMenuRadioItemProps,
	IDropdownMenuLabelProps,
	IDropdownMenuSubTriggerProps,
	IDropdownMenuTriggerProps,
};
